import {getQueryParams} from "@COMPONENTS/common/commonUtil";
import {commonEventTypeCd, commonTpCd} from "@COMPONENTS/common/constants";
import {getPrice} from "@COMPONENTS/common/eventUtil";
import {getExpectMileage} from "@COMPONENTS/common/goodsUtil";
import {formatForTagging, menuForTagging, getTaggingData} from "@COMPONENTS/common/tagging/taggingCommon";
import {CouponPopType} from "@COMPONENTS/product/common/popup/desktop/CouponPopup";
import {CouponByEvent} from "@QUERIES/prdt/coupon/useCouponByEvent";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import dayjs from "dayjs";

/** 상세 데이터 */
export const detailForTagging = (eventDetail: EventDetail) => {
	try {
		const menuData = menuForTagging();
		const queryParams = getQueryParams();
		const queryEvCd = queryParams?.evCd as string | undefined;

		const {evCd, evNm, dayCnt, dayStayInfo, tourPatInfo} = eventDetail;
		const {continentCd, continentNm, countryCd, countryNm} = eventDetail;
		const goodsCd = evCd.split("-")[0];
		// USE_EVENT_PRICE
		const useEventPrice = getTaggingData("useEventPrice");
		const totalPrice = getPrice(useEventPrice);
		const price = {
			adtPrice: useEventPrice?.adtPrice ?? 0,
			chdPrice: useEventPrice?.chdPrice ?? 0,
			infPrice: useEventPrice?.infPrice ?? 0,
		};

		// USE_EVENT_MILEAGE
		const useEventMileage = getTaggingData("useEventMileage");
		const mileage = getExpectMileage(useEventMileage);

		const {evStartDt, outDeprtTm, outArrvTm, outDayDiff, evArriveDt, inDeprtTm, inArrvTm, inDayDiff} = eventDetail;

		const outFlightDeprtTm = formatForTagging(evStartDt, outDeprtTm);
		const outFlightArrvTm = formatForTagging(evStartDt, outArrvTm, outDayDiff);
		const inFlightDeprtTm = formatForTagging(evArriveDt, inDeprtTm, -inDayDiff);
		const inFlightArrvTm = formatForTagging(evArriveDt, inArrvTm);

		return {
			...menuData,
			prodDiv: queryEvCd ? "evCd" : "goodsCd",
			goodsCd,
			evCd,
			evNm,
			continentCd,
			continentNm,
			countryCd,
			countryNm,
			propCd: goodsCd.at(2) ?? "-",
			propNm: commonTpCd[goodsCd.at(2) ?? ""] ?? "-",
			dayCnt,
			nightCnt: dayStayInfo?.split("박")[0] ?? "-",
			tourInfo: tourPatInfo,
			outFlightCd: eventDetail?.outFlightNm ?? "-",
			outFlightNm: eventDetail?.trCompanyNm ?? "-",
			outFlightDeprtTm: outFlightDeprtTm ?? "-",
			outFlightArrvTm: outFlightArrvTm ?? "-",
			inFlightCd: eventDetail?.inFlightNm ?? "-",
			inFlightNm: eventDetail?.trCompanyNm ?? "-",
			inFlightDeprtTm: inFlightDeprtTm ?? "-",
			inFlightArrvTm: inFlightArrvTm ?? "-",
			progCd: eventDetail?.evProgDivi ?? "-",
			progNm: eventDetail?.evProgDiviNm ?? "-",
			recCd: eventDetail?.eventTypeCd ?? "-",
			recNm: commonEventTypeCd[eventDetail?.eventTypeCd] ?? "-",
			adultPrice: price.adtPrice,
			childPrice: price.chdPrice,
			infantPrice: price.infPrice,
			adultSalePrice: totalPrice.adult,
			childSalePrice: totalPrice.child,
			infantSalePrice: totalPrice.infant,
			expectPoint: mileage,
		};
	} catch (error) {
		return null;
	}
};

/**
 * braze, ampliture - pkg_view_dtl
 * @description 행사상세 로드
 */
export const taggingLoadDetailPackage = (eventDetail: EventDetail) => {
	try {
		const detail = detailForTagging(eventDetail);
		const {dspMenuCd, dspMenuNm, dspSidCd, dspSidNm, progNm, evCd} = detail;
		const {continentNm, countryNm, outFlightNm, outFlightDeprtTm, inFlightArrvTm} = detail;

		return {
			data: detail,
			attribute: {
				date: dayjs().format("YYYYMMDDHHmmss"),
				dspMenuCd,
				dspMenuNm,
				dspSidCd,
				dspSidNm,
				evCd,
				continentNm,
				countryNm,
				outFlightNm,
				outFlightDeprtTm,
				inFlightArrvTm,
				progNm,
			},
		};
	} catch (e) {
		return {data: null, attribute: null};
	}
};

/**
 * ampliture - pkg_select_dtl_change
 * @description [출발일 변경] 버튼 클릭
 */
export const taggingClickEventListPopup = (eventDetail: EventDetail) => {
	try {
		const detail = detailForTagging(eventDetail);
		return detail;
	} catch (error) {
		return null;
	}
};

/**
 * braze, ampliture - goods_down_coupon
 * @description 사용가능쿠폰 內 [다운로드] 버튼 클릭 -> 행사상세 중단 & 쿠폰적용 모달
 */
export const taggingClickCouponDown = (coupon: CouponByEvent & {evCd: string; selectDiv: string}) => {
	try {
		const {evCd, selectDiv, couponNm, couponNo, discAmt, discRate, validityFrDt, validityToDt} = coupon;
		return {
			selectDiv,
			evCd,
			couponCd: couponNo,
			couponNm,
			couponValidityFrDt: validityFrDt || "-",
			couponValidityToDt: validityToDt || "-",
			couponDiscRate: discRate || 0,
			couponDiscAmt: discAmt || 0,
		};
	} catch (error) {
		return null;
	}
};

/**
 * pkg_share_dtl
 * @description [공유하기] 버튼 클릭 후 -> 공유 채널 / 클릭
 */
export const taggingClickShareEvent = (eventDetail: EventDetail & {shareCh: string}) => {
	try {
		const detail = detailForTagging(eventDetail);
		return {...detail, shareCh: eventDetail.shareCh};
	} catch (error) {
		return null;
	}
};

/**
 * 행사코드 찜 선택
 * braze - pkg_add_evCd_wish
 * apmplitude - pkg_select_evCd_wish
 * @description [♡] 버튼 (=위시리스트) / 클릭
 */
export const taggingClickEventWish = (eventDetail: EventDetail & {wishYn: string}) => {
	try {
		const detail = detailForTagging(eventDetail);
		return {...detail, wishYn: eventDetail.wishYn};
	} catch (error) {
		return null;
	}
};

/**
 * braze, amplitude
 * 	click	pkg_select_evCd_pkgDtl
 * @description [예약하기] 버튼 클릭
 */
export const taggingClickEventReserve = (
	props: {eventDetail: EventDetail} & {count: {adultCnt: number; childCnt: number; infantCnt: number}} & {
		coupon: CouponPopType[];
	},
) => {
	try {
		const {eventDetail, count, coupon = []} = props;
		const detail = detailForTagging(eventDetail);

		// 쿠폰
		const couponCd = coupon.map(c => c.couponNo).join(",") || "-";
		const couponNm = coupon.map(c => c.couponNm).join(",") || "-";
		const couponDiscDivi = coupon.map(c => c.discDiviCd).join(",") || "-";
		const couponPrice = coupon.map(c => c.calcObj.totalPrice).join(",") || "-";

		// 인원수, 금액
		const {adultCnt, childCnt, infantCnt} = count;
		const totalCnt = adultCnt + childCnt + infantCnt;
		const finPrice =
			detail.adultSalePrice * adultCnt + detail.childSalePrice * childCnt + detail.infantSalePrice * infantCnt;
		return {
			...detail,
			adultCnt,
			childCnt,
			infantCnt,
			couponCd,
			couponNm,
			couponDiscDivi,
			couponPrice,
			finPrice,
			totalCnt,
		};
	} catch (error) {
		return {};
	}
};
